import { EDGE } from '../../constants/edges';
import { makeLiveAccessor, StoreAccessor } from '../StoreAccessor';
import type { Edge, EdgeType } from '../../types';

class EdgeService {
  constructor(private readonly store: StoreAccessor = makeLiveAccessor()) {}

  private getStore() {
    return this.store.getState();
  }

  getSelectedEdgeIds(): string[] {
    const { selectedItems, edges } = this.getStore();
    return selectedItems.filter(id => edges.some(ed => ed.id === id));
  }

  getEdgeById(id: string): Edge | undefined {
    return this.getStore().edges.find(e => e.id === id);
  }

  increaseThickness(edgeIds: string[]): void {
    const { edges, updateEdge } = this.getStore();
    edgeIds.forEach(id => {
      const edge = edges.find(e => e.id === id);
      if (!edge) return;
      const current = edge.strokeWidth ?? EDGE.DEFAULT_STROKE_WIDTH;
      const next = EDGE.STROKE_STEPS.find(s => s > current) ?? EDGE.STROKE_STEPS[EDGE.STROKE_STEPS.length - 1];
      updateEdge(id, { strokeWidth: next });
    });
  }

  decreaseThickness(edgeIds: string[]): void {
    const { edges, updateEdge } = this.getStore();
    edgeIds.forEach(id => {
      const edge = edges.find(e => e.id === id);
      if (!edge) return;
      const current = edge.strokeWidth ?? EDGE.DEFAULT_STROKE_WIDTH;
      const next = [...EDGE.STROKE_STEPS].reverse().find(s => s < current) ?? EDGE.STROKE_STEPS[0];
      updateEdge(id, { strokeWidth: next });
    });
  }

  cycleDashStyle(edgeIds: string[]): void {
    const { edges, updateEdge } = this.getStore();
    edgeIds.forEach(id => {
      const edge = edges.find(e => e.id === id);
      if (!edge) return;
      const current = edge.dashStyle ?? 'solid';
      const idx = EDGE.DASH_CYCLE.indexOf(current);
      const next = EDGE.DASH_CYCLE[(idx + 1) % EDGE.DASH_CYCLE.length];
      updateEdge(id, { dashStyle: next });
    });
  }

  setColor(edgeIds: string[], color: string): void {
    const { updateEdge } = this.getStore();
    edgeIds.forEach(id => {
      updateEdge(id, { color });
    });
  }

  cycleEdgeType(edgeIds: string[]): void {
    const { edges, updateEdge } = this.getStore();
    edgeIds.forEach(id => {
      const edge = edges.find(e => e.id === id);
      if (!edge) return;
      const current: EdgeType = edge.edgeType ?? 'arrow';
      const idx = EDGE.EDGE_TYPE_CYCLE.indexOf(current);
      const next = EDGE.EDGE_TYPE_CYCLE[(idx + 1) % EDGE.EDGE_TYPE_CYCLE.length];
      updateEdge(id, { edgeType: next });
    });
  }

  toggleAnimation(edgeIds: string[]): void {
    const { edges, updateEdge } = this.getStore();
    const anyAnimated = edgeIds.some(id => edges.find(e => e.id === id)?.animated);
    edgeIds.forEach(id => {
      updateEdge(id, { animated: !anyAnimated });
    });
  }

  toggleAllAnimation(): void {
    const { edges, updateEdge } = this.getStore();
    if (edges.length === 0) return;
    const animatedCount = edges.filter(e => e.animated).length;
    const turnOn = animatedCount <= edges.length / 2;
    edges.forEach(edge => {
      updateEdge(edge.id, { animated: turnOn });
    });
  }

  removeEdgesForNodes(nodeIds: string[]): string[] {
    const { edges, removeEdge } = this.getStore();
    const edgesToRemove = edges.filter(e =>
      nodeIds.includes(e.sourceNodeId) || nodeIds.includes(e.targetNodeId)
    );
    edgesToRemove.forEach(e => removeEdge(e.id));
    return edgesToRemove.map(e => e.id);
  }
}

export const edgeService = new EdgeService();
